<template>
  <article>
    <MastheadAlt
      subtitle="Link in bio for Creators / Influencers"
      title="Create your space on the web. Monetize your audience."
      text="Creators love the flexibility and features of Campsite.bio for their
            link in bio. Branding options, tip jars, take requests, and share
            everywhere you are in one place."
      ctaText="(Get your profile set up in around 10 minutes)"
      :profile-images="[
        '/campsites/creators/campsite-beccajamesuk.png',
        '/campsites/creators/campsite-cassierandolph.png',
        '/campsites/creators/campsite-chrisbethea.png',
        '/campsites/creators/campsite-copycompass.png',
        '/campsites/creators/campsite-dothedamnthing.png',
        '/campsites/creators/campsite-gisellemays.png',
        '/campsites/creators/campsite-jamiebareno.png',
        '/campsites/creators/campsite-heyjessica.png',
        '/campsites/creators/campsite-organicallybecca.png',
        '/campsites/creators/campsite-thewinestylings.png',
        '/campsites/creators/campsite-artisiq.png',
        '/campsites/creators/campsite-alexokoroji.png',
        '/campsites/creators/campsite-mattevans.png',
        '/campsites/creators/campsite-adionnewilliams.png',
      ]"
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started FREE</Button
      >
    </MastheadAlt>

    <SeenOn />

    <TestimonialSection
      title="Campsite.bio has helped my business and my audience grow"
      img-src="/campsites/creators/campsite-choicehacking.png"
      name="Jennifer Clinehens"
    >
      <p>
        Having a professional link in bio page is really important to my
        business since most of my traffic comes from different social channels
        like Instagram, Twitter, and LinkedIn - Campsite's features have helped
        me look professional and their embed features have increased my email
        opt-ins, product sales, and YouTube and podcast downloads.
      </p>
      <p>
        I can't recommend Campsite enough if you're looking for a professional
        link in bio to grow your business.
      </p>
    </TestimonialSection>

    <LargeTextSection
      >As a creator you're a busy person. Spend less time updating your bio link
      and more time doing what you do best, creating great content. Campsite.bio
      makes it easy to manage your bio link, while looking professional for your
      audience.</LargeTextSection
    >

    <FeatureSection title="Take your link in bio to the next level">
      <VText slot="text"
        >Get a professional looking bio link with Campsite.
      </VText>

      <FeatureCard
        span="2"
        title="Customization features"
        bg-color="secondary"
        color="secondary"
      >
        <VisibilityIcon slot="icon" />
        <VText
          >Showing off your personality is important. Campsite.bio gives you the
          customization tools to make your bio link stand out and look
          professional.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/creators/appearance-page.png"
          loading="lazy"
          alt="Appearance page"
        />
      </FeatureCard>
      <FeatureCard title="Monetization features">
        <PaymentIcon slot="icon" />
        <VText
          >Collect tips and take requests from your followers right on your bio
          link. A single source for your followers to support you.
        </VText>
      </FeatureCard>
      <FeatureCard title="Link types">
        <GridIcon slot="icon" />
        <VText
          >Use our suite of link types to power your link in bio. Video and
          music embeds, tip jars, requests, an image grid, and more.
        </VText>
      </FeatureCard>
    </FeatureSection>

    <FeatureSection title="Join the Pros" bg-color="gray100">
      <VText slot="text"
        >Get all the features you need in a link in bio app with our Pro
        subscription.</VText
      >
      <Button slot="cta" to="/pricing">Compare features</Button>

      <FeatureCard title="Automation">
        <SettingsIcon slot="icon" />
        <VText
          >Keep your profile fresh with our automated link types, like the feed
          and image grid. They'll pull in the newest content from Youtube, a
          blog, or Instagram, automatically.
        </VText>
      </FeatureCard>
      <FeatureCard title="Premium link types">
        <GridIcon slot="icon" />
        <VText
          >Unlock our best link types with multiple carousels, opt-in forms,
          feed links, forms (Typeform and Jotform), image grids and so much
          more.</VText
        >
      </FeatureCard>
      <FeatureCard title="Scheduling links">
        <EventIcon slot="icon" />
        <VText
          >Do you have timed promotions or events? Put up and take down your
          links at specific times with our scheduling features.
        </VText>
      </FeatureCard>
      <FeatureCard title="Highlight a link">
        <StarIcon slot="icon" />
        <VText
          >Draw attention to your most important link with a highlight
          animation.
        </VText>
      </FeatureCard>
      <FeatureCard title="Multiple profiles">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Do you have multiple brands or businesses? Manage them all from one
          account with our multiple profiles feature.
        </VText>
      </FeatureCard>
      <FeatureCard title="Custom domain">
        <WebsiteIcon slot="icon" />
        <VText
          >Show your audience a professional link in bio page with your own URL,
          no Campsite.bio to be found (ex. links.mydomain.com).
        </VText>
      </FeatureCard>
    </FeatureSection>

    <RaisedCtaSection
      title="Simplify your link in bio management today"
      text="Start taking your link in bio seriously, with a professional looking Campsite.bio profile."
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Button,
  AccountMultipleIcon,
  GridIcon,
  SettingsIcon,
  PaymentIcon,
  VisibilityIcon,
  EventIcon,
  StarIcon,
  WebsiteIcon,
} from '@campsite-bio/component-lib';

import SeenOn from '@/components/seen-on-analytics';
import TestimonialSection from '@/components/testimonials/testimonial-section';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import LargeTextSection from '@/components/sections/large-text-section';
import MastheadAlt from '@/components/sections/masthead-alt';
import { generateMeta } from '~/utils/generate-meta';

export default {
  components: {
    VText,
    Button,
    SeenOn,
    TestimonialSection,
    FeatureCard,
    FeatureSection,
    AccountMultipleIcon,
    GridIcon,
    SettingsIcon,
    RaisedCtaSection,
    LargeTextSection,
    MastheadAlt,
    PaymentIcon,
    VisibilityIcon,
    EventIcon,
    StarIcon,
    WebsiteIcon,
  },

  head() {
    return generateMeta({
      title: 'Link in bio for Creators - Campsite.bio',
      description:
        'Create your space on the web. Learn more about the features Campsite.bio offers to creators.',
    });
  },
};
</script>

<style lang="scss" scoped></style>
